// Per-line price explanation: the engine's own chain for ONE quote line,
// step by step, with the operation between steps and the rounding
// checkpoints called out.
//
// THIS PANEL DOES NO ARITHMETIC. Every number below is read off the stored
// LivePricedLine (or off the Material row for the inputs the app itself
// sent). The `op` string on each step is TEXT naming what the engine did to
// get from the previous value to this one — it is never evaluated, and a
// step whose value the backend did not return is OMITTED rather than
// derived. That is the whole point: a panel that recomputed the chain to
// fill a gap would be a second copy of the pricing math, which is exactly
// what the /engine hard rule exists to prevent.
//
// Absent is rendered as absent, never as 0. On the domestic path 0 is also a
// legitimate inbound_freight, so a missing intermediate (a reopened quote
// carries no inbound_freight / cost_with_inbound_freight / total — see
// LivePricedLine in services/api.ts) must not read as "the engine said
// zero".
//
// Visual vocabulary is SidePanel's CustomsSection: label/value flex pairs,
// greyed non-counting lines with an italic caption, a ruled total at the
// bottom. Shell is the shared DetailPanel, like the other read-only panel
// (InventoryDetailPanel) — title + close header, scrollable body, one Close
// button.

import type { Material } from "@/types";
import type { LivePricedLine } from "@/services/api";
import type { PricingMode } from "@/components/MaterialTable";
import { DetailPanel } from "@/components/DetailPanel";
import { formatPercent, formatRupees, moneyLocale } from "@/lib/format";
import {
  colors, semanticColors, radius, spacing, heights, typography,
} from "@/styles/tokens";

const LABEL: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textSecondary };
const VALUE: React.CSSProperties = {
  fontSize: typography.fontSize.sm, color: semanticColors.textPrimary,
  fontVariantNumeric: "tabular-nums",
};
const CAPTION: React.CSSProperties = {
  fontSize: typography.fontSize.xs, color: semanticColors.textPlaceholder, fontStyle: "italic",
};

// Supplier-currency money. NOT formatRupees: on the import path the cost
// columns are in whatever the supplier bills in, and conversion_rate is what
// turns them into rupees further down the chain — a "Rs" prefix here would
// misstate the unit by roughly the exchange rate. Same reasoning as
// MaterialTable's "List Price FCY" header.
// The configured money locale (P1-11; en-IN unless Settings says otherwise).
const fcy = (n: number) => n.toLocaleString(moneyLocale(), {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});
// CF is a multiplier and conversion_rate is an FX rate — neither is money.
// 4dp for both, matching MaterialTable's own `factor`/`rate` formatters: the
// identity CF encodes (unit cost x CF == landing cost) only reads as true at
// that precision.
const factor = (n: number) => n.toFixed(4);

// One link in the chain.
type Step = {
  key: string;
  label: string;
  // The operation that produced this value FROM THE PREVIOUS STEP, as text.
  // Absent on the first row, which is an input rather than a result.
  op?: string;
  // undefined means the backend did not return it. The row is dropped.
  value: number | undefined;
  format: (n: number) => string;
  // A rounding checkpoint the engine applies AT this step. Marked because
  // it is where the result stops being the raw arithmetic — the domestic
  // path's whole-rupee Unit SP is where 10246.5 becomes 10247.
  rounding?: { pill: string; note: string };
  // A line-level figure that the per-unit chain does not flow through.
  // Greyed with a caption, exactly like CustomsSection's IGST row.
  aside?: string;
  // The line's final answer.
  emphasis?: boolean;
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
      textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
      fontWeight: typography.fontWeight.medium, marginBottom: spacing[8],
    }}>
      {children}
    </div>
  );
}

function KVRow({ label, value, testid }: { label: string; value: React.ReactNode; testid?: string }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span style={LABEL}>{label}</span>
      <span style={VALUE}>{value}</span>
    </div>
  );
}

// Informational pill — indigo, per the pill colour scheme (indigo =
// informational). Marks a rounding checkpoint.
function RoundingPill({ text, testid }: { text: string; testid?: string }) {
  return (
    <span
      data-testid={testid}
      style={{
        height: heights.pill, padding: `0 ${spacing[8]}px`, borderRadius: radius.base,
        fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
        backgroundColor: colors.indigo50, color: semanticColors.accentTextOnTint,
        border: `1px solid ${colors.indigo100}`,
        display: "inline-flex", alignItems: "center", whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
}

function StepRow({ step }: { step: Step }) {
  // Callers filter these out, but the type says undefined is possible and a
  // stray 0 in this column would be a lie, so the guard stays here too.
  if (typeof step.value !== "number") return null;
  const muted = !!step.aside;

  return (
    <div data-testid={`explain-step-${step.key}`}>
      {step.op && (
        <div
          data-testid={`explain-op-${step.key}`}
          style={{
            fontSize: typography.fontSize.xs, color: semanticColors.textPlaceholder,
            fontVariantNumeric: "tabular-nums", padding: `${spacing[2]}px 0`,
          }}
        >
          {step.op}
        </div>
      )}
      <div className="flex items-center justify-between" style={{ gap: spacing[8] }}>
        <span style={{
          ...LABEL,
          color: muted ? semanticColors.textPlaceholder : semanticColors.textSecondary,
        }}>
          {step.label}
        </span>
        <span className="flex items-center" style={{ gap: spacing[6] }}>
          {step.rounding && (
            <RoundingPill text={step.rounding.pill} testid={`explain-rounding-${step.key}`} />
          )}
          <span
            data-testid={`explain-value-${step.key}`}
            style={{
              ...VALUE,
              color: muted ? semanticColors.textPlaceholder : semanticColors.textPrimary,
              fontSize: step.emphasis ? typography.fontSize.lg : typography.fontSize.sm,
              fontWeight: step.emphasis ? typography.fontWeight.semibold : typography.fontWeight.regular,
            }}
          >
            {step.format(step.value)}
          </span>
        </span>
      </div>
      {step.aside && <div style={CAPTION}>{step.aside}</div>}
      {step.rounding && (
        <div data-testid={`explain-rounding-note-${step.key}`} style={CAPTION}>
          {step.rounding.note}
        </div>
      )}
    </div>
  );
}

// The DOMESTIC chain, in the engine's own order:
//   unit price -> extd price -> inbound freight -> cost with inbound freight
//   -> total -> Cost With SV -> Unit SP -> Extended SP
//
// A reopened quote carries only cost_with_sv out of the middle four, so the
// visible chain there is net price -> Cost With SV -> Unit SP -> Extended SP.
// Nothing reconstructs the missing steps from ibf_pct.
function domesticSteps(line: Material, priced: LivePricedLine): Step[] {
  const v = priced.variance_pct_snapshot;
  const g = priced.gp_pct_snapshot;
  return [
    {
      key: "netPrice", label: "Net Price (unit cost)", value: line.netPrice, format: formatRupees,
    },
    {
      key: "extdPrice", label: "Extd Price", op: `x qty ${line.qty}`,
      value: priced.extd_price, format: formatRupees,
      aside: "line-level figure — the per-unit chain continues from Net Price.",
    },
    {
      key: "inboundFreight", label: "Inbound Freight",
      op: "x IBF % (0 on the domestic path)",
      value: priced.inbound_freight, format: formatRupees,
    },
    {
      key: "costWithInboundFreight", label: "Cost with Inbound Freight",
      op: "Net Price + Inbound Freight",
      value: priced.cost_with_inbound_freight, format: formatRupees,
    },
    {
      key: "total", label: "Total", op: `x qty ${line.qty}`,
      value: priced.total, format: formatRupees,
      aside: "line-level figure — the per-unit chain continues from Cost with Inbound Freight.",
    },
    {
      key: "costWithSv", label: "Cost With SV",
      op: `x (1 + variance ${formatPercent(v)})`,
      value: priced.cost_with_sv, format: formatRupees,
      rounding: {
        pill: "rounded 2 dp",
        note: "ROUND_HALF_UP to 2 decimal places. This rounded value — not the raw product — is what Unit SP divides.",
      },
    },
    {
      key: "unitSp", label: "Unit SP",
      op: `/ (1 - GP ${formatPercent(g)})`,
      value: priced.unit_sp, format: formatRupees,
      rounding: {
        pill: "rounded whole Rs",
        // DELIBERATELY STATIC — 10246.5 is validated row 4's raw quotient,
        // quoted to show what the rule does. It is NOT this line's pre-round
        // value and must never be interpolated into one: the engine does not
        // return the unrounded quotient, so the only way to put this line's
        // own raw figure here would be to recompute the division, which is
        // the pricing math this panel exists not to duplicate.
        note: "ROUND_HALF_UP to the nearest whole rupee — a raw 10246.5 lands here as 10247, not 10246.",
      },
    },
    {
      key: "extendedSp", label: "Extended SP", op: `rounded Unit SP x qty ${line.qty}`,
      value: priced.extended_sp, format: formatRupees, emphasis: true,
    },
  ];
}

// The IMPORT chain, in the engine's own order:
//   unit cost -> ext cost -> freight value -> freight+unit -> value in INR
//   -> landing cost -> landing+variance -> total landing cost, then
//   Unit SP / Extended SP off landing+variance.
//
// The cost side is in the SUPPLIER's currency until Value in INR; the
// formatters below switch there and not before.
function importSteps(line: Material, priced: LivePricedLine): Step[] {
  const v = priced.variance_pct_snapshot;
  const g = priced.gp_pct_snapshot;
  // The three per-line import inputs, as the app sent them. An unfilled one
  // is sent as 0 (see materialToBackendLine), and MaterialTable's own import
  // columns show that 0 for the same reason: what the line was priced with
  // should be on screen. This is not the absent-as-absent case above, which
  // is about ENGINE OUTPUTS the backend did not return.
  const f = line.freightPct ?? 0;
  const r = line.conversionRate ?? 0;
  const d = line.dutyPct ?? 0;
  return [
    {
      key: "unitCost", label: "Unit Cost (FCY)", value: line.netPrice, format: fcy,
    },
    {
      key: "extCost", label: "Ext. Cost (FCY)", op: `x qty ${line.qty}`,
      value: priced.extd_price, format: fcy,
    },
    {
      key: "freightValue", label: "Freight Value (FCY)",
      op: `Ext. Cost x freight ${formatPercent(f)}`,
      value: priced.freight_value, format: fcy,
    },
    {
      key: "freightPlusUnit", label: "Freight+Unit (FCY)",
      op: `(Ext. Cost + Freight Value) / qty ${line.qty}`,
      value: priced.freight_plus_unit, format: fcy,
    },
    {
      key: "valueInInr", label: "Value in INR",
      op: `x conversion rate ${factor(r)}`,
      value: priced.value_in_inr, format: formatRupees,
    },
    {
      key: "landingCost", label: "Landing Cost",
      op: `x (1 + duty ${formatPercent(d)})`,
      value: priced.landing_cost, format: formatRupees,
    },
    {
      key: "landingPlusVariance", label: "Landing+Var",
      op: `x (1 + variance ${formatPercent(v)})`,
      value: priced.landing_plus_variance, format: formatRupees,
    },
    {
      key: "totalLandingCost", label: "Total Landing Cost", op: `x qty ${line.qty}`,
      value: priced.total_landing_cost, format: formatRupees,
      aside: "line-level figure — the sell price is taken from Landing+Var, not from this.",
    },
    {
      key: "unitSp", label: "Unit SP",
      op: `Landing+Var / (1 - GP ${formatPercent(g)})`,
      value: priced.unit_sp, format: formatRupees,
      rounding: {
        pill: "rounded whole Rs",
        note: "ROUND_HALF_UP to the nearest whole rupee. The only place rounding enters the import result — every step above ran at full precision and is shown at the engine's own 2 dp.",
      },
    },
    {
      key: "extendedSp", label: "Extended SP", op: `rounded Unit SP x qty ${line.qty}`,
      value: priced.extended_sp, format: formatRupees, emphasis: true,
    },
  ];
}

export function PriceExplainPanel({
  line,
  priced,
  mode,
  onClose,
}: {
  line: Material;
  // The stored engine result for this line. The panel is only rendered for a
  // line that has one — an unpriced line has nothing to explain.
  priced: LivePricedLine;
  mode: PricingMode;
  onClose: () => void;
}) {
  const isImport = mode === "import";
  const steps = (isImport ? importSteps(line, priced) : domesticSteps(line, priced))
    // Absent-as-absent: a step the backend did not return is dropped, not
    // shown as 0 and not derived from its neighbours.
    .filter((s) => typeof s.value === "number");

  return (
    <DetailPanel
      testIdPrefix="explain"
      title={`Line ${line.lineNo} · ${line.partNo}`}
      subtitle={line.description}
      onClose={onClose}
      footer={
        <button
          data-testid="explain-panel-close-btn"
          className="btn-secondary"
          onClick={onClose}
          style={{
            height: heights.control, padding: `0 ${spacing[14]}px`,
            backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
            fontSize: typography.fontSize.base, cursor: "pointer",
          }}
        >
          Close
        </button>
      }
    >
      {/* Inputs — including the two snapshots, which are the authoritative
          record of what THIS line was priced with. A quote's pricing config
          can be edited afterwards without restating priced lines, so the
          config's percentages today and these can legitimately differ. */}
      <div>
        <SectionLabel>Inputs</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: spacing[6] }}>
          <KVRow label="OEM" value={line.oem} testid="explain-input-oem" />
          <KVRow label="Qty" value={line.qty} testid="explain-input-qty" />
          <KVRow
            label={isImport ? "Unit Cost (FCY)" : "Net Price Rs"}
            value={isImport ? fcy(line.netPrice) : formatRupees(line.netPrice)}
            testid="explain-input-unit-cost"
          />
          {isImport && (
            <>
              <KVRow label="Freight %"  value={formatPercent(line.freightPct ?? 0)}   testid="explain-input-freight-pct" />
              <KVRow label="Conv. Rate" value={factor(line.conversionRate ?? 0)}      testid="explain-input-conversion-rate" />
              <KVRow label="Duty %"     value={line.dutyPct === undefined ? "not set" : formatPercent(line.dutyPct)} testid="explain-input-duty-pct" />
            </>
          )}
          <KVRow label="Std Variance % (snapshot)" value={formatPercent(priced.variance_pct_snapshot)} testid="explain-input-variance" />
          <KVRow label="GP % (snapshot)"           value={formatPercent(priced.gp_pct_snapshot)}       testid="explain-input-gp" />
        </div>
        <div style={{ ...CAPTION, marginTop: spacing[6] }}>
          The two snapshots are what this line was priced with, not what the
          pricing config says today.
        </div>
      </div>

      {/* The chain */}
      <div style={{ marginTop: spacing[16], paddingTop: spacing[14], borderTop: `1px solid ${semanticColors.border}` }}>
        <div className="flex items-center justify-between" style={{ marginBottom: spacing[8] }}>
          <SectionLabel>Pricing chain</SectionLabel>
          <span
            data-testid="explain-mode-pill"
            style={{
              height: heights.pill, padding: `0 ${spacing[8]}px`, borderRadius: radius.base,
              fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
              backgroundColor: colors.indigo50, color: semanticColors.accentTextOnTint,
              border: `1px solid ${colors.indigo100}`,
              display: "inline-flex", alignItems: "center",
              textTransform: "capitalize",
            }}
          >
            {mode}
          </span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: spacing[10] }}>
          {steps.map((s) => <StepRow key={s.key} step={s} />)}
        </div>
      </div>

      {/* Import-only cross-check. CF is not a step in the chain — it is the
          identity the client's own sheet carries over the whole
          freight/FX/duty stretch, and engine/test_import_pricing.py asserts
          it on both real rows. Shown only when the engine returned it. */}
      {isImport && typeof priced.cf === "number" && (
        <div style={{ marginTop: spacing[16], paddingTop: spacing[14], borderTop: `1px solid ${semanticColors.border}` }}>
          <SectionLabel>Cross-check</SectionLabel>
          <div className="flex items-center justify-between">
            <span style={LABEL}>CF</span>
            <span data-testid="explain-value-cf" style={VALUE}>{factor(priced.cf)}</span>
          </div>
          {/* The identity is stated with NAMED operands, deliberately — no
              numbers are substituted into it. Landing Cost (unrounded) is not
              something the engine returns (the result carries the 2dp display
              figure), so demonstrating the identity numerically here would
              mean multiplying it out locally. The value above is the engine's
              own cf; the sentence below explains what it means. */}
          <div style={{ ...CAPTION, marginTop: spacing[4] }}>
            CF = (1 + Freight %) x Conversion Rate x (1 + Duty %). It encodes the
            identity Unit Cost x CF == Landing Cost (unrounded), which makes it an
            independent check on the whole freight/FX/duty stretch above rather
            than a restatement of it.
          </div>
        </div>
      )}

      <div style={{
        marginTop: spacing[16], paddingTop: spacing[14],
        borderTop: `1px solid ${semanticColors.border}`, ...CAPTION,
      }}>
        Every figure above is the engine's own. This panel names the operation
        between steps; it does not recompute them. A step the backend did not
        return is omitted rather than shown as zero.
      </div>
    </DetailPanel>
  );
}
